import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { MoreHorizontal, ChevronLeft, ChevronRight, Search, Users } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { AdminUser, AdminMember } from '../types/index.js'
import UserDetailPanel from './UserDetailPanel.js'
import UserActivityDrawer from './UserActivityDrawer.js'

interface Props {
  users: AdminUser[]
  total: number
  page: number
  limit: number
  loading: boolean
  onPageChange: (p: number) => void
  onUpdateUser: (
    id: string,
    payload: Partial<{ name: string; email: string; platformRole: 'user' | 'super_admin'; isActive: boolean }>,
  ) => Promise<void>
  onResetPassword: (userId: string, payload: { password: string; confirm_password: string }) => Promise<void>
  onAddToWorkspace: (
    userId: string,
    payload: { workspaceId: string; role: 'owner' | 'admin' | 'member' },
  ) => Promise<AdminMember>
  onUpdateMembershipRole: (
    userId: string,
    membershipId: string,
    role: 'owner' | 'admin' | 'member',
  ) => Promise<AdminMember>
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

const SkeletonRow: React.FC<{ border: string; secondary: string }> = ({ border, secondary }) => (
  <tr style={{ borderTop: `1px solid ${border}` }}>
    <td className="px-5 py-4">
      <div className="space-y-2">
        <div className="h-3.5 rounded-full animate-pulse w-32" style={{ backgroundColor: border }} />
        <div className="h-3 rounded-full animate-pulse w-44" style={{ backgroundColor: secondary }} />
      </div>
    </td>
    <td className="px-5 py-4">
      <div className="h-6 rounded-full animate-pulse w-20" style={{ backgroundColor: border }} />
    </td>
    <td className="px-5 py-4">
      <div className="h-6 rounded-full animate-pulse w-16" style={{ backgroundColor: border }} />
    </td>
    <td className="px-5 py-4">
      <div className="h-3.5 rounded-full animate-pulse w-24" style={{ backgroundColor: border }} />
    </td>
    <td className="px-5 py-4">
      <div className="h-8 w-8 rounded-lg animate-pulse" style={{ backgroundColor: border }} />
    </td>
  </tr>
)

type DropdownPos = { top: number; right: number }

const UserTable: React.FC<Props> = ({
  users,
  total,
  page,
  limit,
  loading,
  onPageChange,
  onUpdateUser,
  onResetPassword,
  onAddToWorkspace,
  onUpdateMembershipRole,
}) => {
  const { currentTheme } = useTheme()
  const [search, setSearch] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [logsUser, setLogsUser] = useState<AdminUser | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (!search.trim()) return users
    const q = search.toLowerCase()
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [users, search])

  const totalPages = Math.max(1, Math.ceil(total / limit))

  // Close dropdown on outside click or scroll
  useEffect(() => {
    if (!openDropdown) return
    const close = () => { setOpenDropdown(null); setDropdownPos(null) }
    window.addEventListener('scroll', close, true)
    return () => window.removeEventListener('scroll', close, true)
  }, [openDropdown])

  const handleToggleDropdown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, userId: string) => {
      e.stopPropagation()
      if (openDropdown === userId) {
        setOpenDropdown(null)
        setDropdownPos(null)
        return
      }
      const rect = e.currentTarget.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      })
      setOpenDropdown(userId)
    },
    [openDropdown],
  )

  const handleAction = useCallback(
    async (
      id: string,
      payload: Partial<{ name: string; email: string; platformRole: 'user' | 'super_admin'; isActive: boolean }>,
    ) => {
      setActionLoading(id)
      setOpenDropdown(null)
      setDropdownPos(null)
      try {
        await onUpdateUser(id, payload)
      } finally {
        setActionLoading(null)
      }
    },
    [onUpdateUser],
  )

  const handleRowClick = useCallback((user: AdminUser) => {
    setSelectedUser(user)
    setOpenDropdown(null)
    setDropdownPos(null)
  }, [])

  const handleViewLogs = useCallback((user: AdminUser) => {
    setLogsUser(user)
    setOpenDropdown(null)
    setDropdownPos(null)
  }, [])

  const selectedId = selectedUser?.id
  const syncedSelected = useMemo(
    () => (selectedId ? (users.find((u) => u.id === selectedId) ?? selectedUser) : selectedUser),
    [selectedId, users, selectedUser],
  )

  const openUser = filtered.find((u) => u.id === openDropdown)

  return (
    <div>
      {/* Search */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: currentTheme.mutedText }}
          />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border pl-9 pr-4 py-2.5 text-sm outline-none transition"
            style={{
              backgroundColor: currentTheme.inputBackground,
              color: currentTheme.text,
              borderColor: currentTheme.border,
            }}
          />
        </div>
        {search && (
          <span className="text-xs shrink-0" style={{ color: currentTheme.mutedText }}>
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Table */}
      <div
        className="rounded-2xl border overflow-hidden"
        style={{ borderColor: currentTheme.border }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <colgroup>
              <col className="w-auto" />
              <col style={{ width: '140px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '56px' }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: currentTheme.secondary }}>
                {['Name', 'Role', 'Status', 'Created', ''].map((h, i) => (
                  <th
                    key={i}
                    className={`px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider${i === 4 ? ' text-right' : ''}`}
                    style={{ color: currentTheme.secondrytext }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonRow key={i} border={currentTheme.border} secondary={currentTheme.secondary} />
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Users size={28} style={{ color: currentTheme.mutedText }} />
                      <p className="text-sm font-medium" style={{ color: currentTheme.secondrytext }}>
                        {search ? 'No users match your search' : 'No users yet'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((user) => (
                  <tr
                    key={user.id}
                    className="transition-colors cursor-pointer"
                    style={{ borderTop: `1px solid ${currentTheme.border}` }}
                    onClick={() => handleRowClick(user)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                        currentTheme.hoverBackground
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'
                    }}
                  >
                    {/* Name + email */}
                    <td className="px-5 py-4">
                      <p className="font-semibold leading-tight" style={{ color: currentTheme.text }}>
                        {user.name}
                      </p>
                      <p className="text-xs mt-0.5 truncate max-w-[240px]" style={{ color: currentTheme.secondrytext }}>
                        {user.email}
                      </p>
                    </td>

                    {/* Role */}
                    <td className="px-5 py-4">
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                        style={
                          user.platformRole === 'super_admin'
                            ? { backgroundColor: `${currentTheme.accent}22`, color: currentTheme.accent }
                            : { backgroundColor: currentTheme.border, color: currentTheme.secondrytext }
                        }
                      >
                        {user.platformRole === 'super_admin' ? 'Super Admin' : 'User'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                        style={
                          user.isActive
                            ? { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                            : { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
                        }
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: user.isActive ? currentTheme.successText : currentTheme.errorText }}
                        />
                        {user.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-5 py-4 whitespace-nowrap text-sm" style={{ color: currentTheme.secondrytext }}>
                      {formatDate(user.createdAt)}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        disabled={actionLoading === user.id}
                        onClick={(e) => handleToggleDropdown(e, user.id)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition disabled:opacity-40"
                        style={{
                          color: currentTheme.secondrytext,
                          backgroundColor: openDropdown === user.id ? currentTheme.hoverBackground : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                            openDropdown === user.id ? currentTheme.hoverBackground : 'transparent'
                        }}
                        aria-label="Actions"
                        aria-expanded={openDropdown === user.id}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dropdown — fixed positioning escapes overflow:hidden on the table container */}
      {openDropdown && dropdownPos && openUser && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => { setOpenDropdown(null); setDropdownPos(null) }}
          />
          <div
            ref={dropdownRef}
            className="fixed z-[70] min-w-[176px] rounded-xl border py-1"
            style={{
              top: dropdownPos.top,
              right: dropdownPos.right,
              backgroundColor: currentTheme.secondary,
              borderColor: currentTheme.border,
              boxShadow: currentTheme.boxShadow,
            }}
          >
            <button
              type="button"
              onClick={() => handleRowClick(openUser)}
              className="w-full text-left px-4 py-2.5 text-sm transition"
              style={{ color: currentTheme.text }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              }}
            >
              View Details
            </button>
            <button
              type="button"
              onClick={() => handleViewLogs(openUser)}
              className="w-full text-left px-4 py-2.5 text-sm transition"
              style={{ color: currentTheme.text }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              }}
            >
              View Logs
            </button>
            <div style={{ height: '1px', backgroundColor: currentTheme.border, margin: '2px 0' }} />
            <button
              type="button"
              onClick={() =>
                handleAction(openUser.id, {
                  platformRole: openUser.platformRole === 'super_admin' ? 'user' : 'super_admin',
                })
              }
              className="w-full text-left px-4 py-2.5 text-sm transition"
              style={{ color: currentTheme.text }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              }}
            >
              {openUser.platformRole === 'super_admin' ? 'Demote to User' : 'Promote to Admin'}
            </button>
            <button
              type="button"
              onClick={() => handleAction(openUser.id, { isActive: !openUser.isActive })}
              className="w-full text-left px-4 py-2.5 text-sm transition"
              style={{ color: openUser.isActive ? currentTheme.errorText : currentTheme.successText }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              }}
            >
              {openUser.isActive ? 'Suspend Account' : 'Activate Account'}
            </button>
          </div>
        </>
      )}

      {/* Pagination */}
      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm" style={{ color: currentTheme.secondrytext }}>
          {total > 0
            ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`
            : '0 users'}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: currentTheme.secondary,
              color: currentTheme.text,
              borderColor: currentTheme.border,
            }}
          >
            <ChevronLeft size={14} />
            Previous
          </button>
          <span className="text-xs px-1" style={{ color: currentTheme.mutedText }}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => onPageChange(page + 1)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: currentTheme.secondary,
              color: currentTheme.text,
              borderColor: currentTheme.border,
            }}
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Detail panel */}
      <UserDetailPanel
        user={syncedSelected}
        onClose={() => setSelectedUser(null)}
        onUpdateUser={onUpdateUser}
        onResetPassword={onResetPassword}
        onAddToWorkspace={onAddToWorkspace}
        onUpdateMembershipRole={onUpdateMembershipRole}
      />

      {/* Activity log drawer */}
      <UserActivityDrawer
        userId={logsUser?.id ?? null}
        userName={logsUser?.name}
        onClose={() => setLogsUser(null)}
      />
    </div>
  )
}

export default React.memo(UserTable)
